import React, { useEffect, useState } from 'react';
import { GetFridgeSchemasTemplates } from '../../libs/GraphQL';
import styles from './SchemaConditionsLanding.module.css';

const SchemaConditionsLanding = () => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemaConditions, setSchemaConditions] = useState([]);
  const [showConditions, setShowConditions] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [allSchemaData, setAllSchemaData] = useState([]); // Cache full schema data

  useEffect(() => {
    const fetchSchemas = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all schema conditions at once
        const result = await GetFridgeSchemasTemplates({});
        // Filter out BASE_PMT schemas (only show schemas with item-based pricing)
        // since the update/create tool only works with item prices
        const filteredData = (result || []).filter(
          (schema) => schema.pricingType !== 'BASE_PMT',
        );
        setAllSchemaData(filteredData); // Cache the full data
        // Extract unique schema IDs for dropdown
        const uniqueSchemas = [
          ...new Set(filteredData.map((schema) => schema.schemaId)),
        ].sort();
        setSchemas(uniqueSchemas);
      } catch (err) {
        console.error('Error fetching schemas:', err);
        setError('Failed to fetch schema IDs.');
      } finally {
        setLoading(false);
      }
    };
    fetchSchemas();
  }, []);

  const handleSchemaChange = (e) => {
    setSelectedSchema(e.target.value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);

    if (value === '' || value === 'All Schemas') {
      setSelectedSchema('ALL');
    } else {
      setSelectedSchema(value);
    }
  };

  const handleSchemaSelect = (schema) => {
    setSearchTerm(schema === 'ALL' ? 'All Schemas' : schema);
    setSelectedSchema(schema);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  // Filter schemas based on search term
  const filteredSchemas = schemas.filter((schema) =>
    schema.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleViewConditions = () => {
    setLoading(true);
    setError(null);
    try {
      // Use cached data instead of fetching again
      let filteredConditions = allSchemaData;
      if (selectedSchema !== 'ALL') {
        filteredConditions = allSchemaData.filter(
          (condition) => condition.schemaId === selectedSchema,
        );
      }
      setSchemaConditions(filteredConditions);
      setShowConditions(true);
    } catch (err) {
      setError('Failed to display schema conditions.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    setLoading(true);
    setError(null);
    try {
      // Use cached data instead of fetching again
      let filteredConditions = allSchemaData;
      if (selectedSchema !== 'ALL') {
        filteredConditions = allSchemaData.filter(
          (condition) => condition.schemaId === selectedSchema,
        );
      }
      if (!filteredConditions.length) {
        setError('No schema conditions found for the selected schema.');
        setLoading(false);
        return;
      }

      // Helper function to escape CSV fields
      const escapeCsvField = (field) => {
        if (field === null || field === undefined || field === '') return '';
        const stringField = String(field);
        // If field contains comma, newline, or quotes, wrap in quotes and escape existing quotes
        if (
          stringField.includes(',') ||
          stringField.includes('\n') ||
          stringField.includes('"')
        ) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      };

      // Helper function to format timeBoundSchedule for CSV
      const formatTimeBoundScheduleForCsv = (schedule) => {
        if (!schedule) return '';
        try {
          const parsed = JSON.parse(schedule);
          return Object.entries(parsed)
            .map(
              ([day, times]) =>
                `${day.toLowerCase()} ${times
                  .map((t) => t.join('-'))
                  .join(', ')}`,
            )
            .join('; ');
        } catch {
          return schedule;
        }
      };

      // Helper function to remove % from discount amount
      const cleanDiscountAmount = (discount) => {
        if (!discount) return '';
        return String(discount).replace('%', '');
      };

      // Create CSV content - column order matches upload format
      const csvHeaders = [
        'schemaId',
        'schemaType',
        'discount',
        'sharedSubsidyClientDiscount',
        'discountSchedule',
        'discountUI',
        'expiringDays',
        'priceDisplayType',
        'platform',
      ];

      const csvContent = [
        csvHeaders.join(','),
        ...filteredConditions.map((row) =>
          [
            escapeCsvField(row.schemaId),
            escapeCsvField(row.schemaType),
            escapeCsvField(cleanDiscountAmount(row.discountAmount)),
            escapeCsvField(
              cleanDiscountAmount(row.sharedSubsidyClientDiscount),
            ),
            escapeCsvField(
              formatTimeBoundScheduleForCsv(row.timeBoundSchedule),
            ),
            row.discountUI ? 'TRUE' : '',
            escapeCsvField(row.expiringDays),
            escapeCsvField(row.priceDisplayType),
            escapeCsvField(row.platform),
          ].join(','),
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'schema_conditions.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download schema conditions.');
    } finally {
      setLoading(false);
    }
  };

  // Format discount amount for table display (adds % symbol)
  const formatDiscountForDisplay = (discount) => {
    if (!discount) return '-';
    return `${discount}%`;
  };

  const formatTimeBoundSchedule = (schedule) => {
    if (!schedule) return '-';
    try {
      const parsed = JSON.parse(schedule);
      const days = Object.entries(parsed).map(
        ([day, times]) => `${day}: ${times.map((t) => t.join('-')).join(', ')}`,
      );

      return (
        <div className={styles.scheduleLines}>
          {days.map((daySchedule, idx) => (
            <div key={idx}>{daySchedule}</div>
          ))}
        </div>
      );
    } catch {
      return schedule;
    }
  };

  // Format schedule for clipboard (plain text)
  const formatTimeBoundScheduleForClipboard = (schedule) => {
    if (!schedule) return '';
    try {
      const parsed = JSON.parse(schedule);
      return Object.entries(parsed)
        .map(
          ([day, times]) =>
            `${day.toLowerCase()} ${times.map((t) => t.join('-')).join(', ')}`,
        )
        .join('; ');
    } catch {
      return schedule;
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      // Create TSV (tab-separated values) format ready for re-upload
      // Using lowercase headers to match upload CSV format
      const headers = [
        'schemaId',
        'schemaType',
        'discount',
        'sharedSubsidyClientDiscount',
        'discountSchedule',
        'discountUI',
        'expiringDays',
        'priceDisplayType',
        'platform',
      ];

      // Helper to remove % from discount amount for easier editing
      const cleanDiscountAmount = (discount) => {
        if (!discount) return '';
        return String(discount).replace('%', '');
      };

      const rows = schemaConditions.map((condition) => [
        condition.schemaId || '',
        condition.schemaType || '',
        cleanDiscountAmount(condition.discountAmount),
        cleanDiscountAmount(condition.sharedSubsidyClientDiscount),
        formatTimeBoundScheduleForClipboard(condition.timeBoundSchedule),
        condition.discountUI ? 'TRUE' : '',
        condition.expiringDays || '',
        condition.priceDisplayType || '',
        condition.platform || '',
      ]);

      const tsvContent = [
        headers.join('\t'),
        ...rows.map((row) => row.join('\t')),
      ].join('\n');

      await navigator.clipboard.writeText(tsvContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <main className={styles.container}>
      <h1>Fridge Schema Conditions</h1>
      <section className={styles.card}>
        <h2>View or download Fridge Schema Conditions</h2>
        <div className={styles.formRow}>
          <label htmlFor="schemaSearch">Filter by Schema:</label>
          <div className={styles.searchContainer}>
            <input
              id="schemaSearch"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Search schema or Select All Schemas"
              className={styles.searchInput}
              disabled={loading}
            />
            {showDropdown && (
              <div className={styles.dropdown}>
                <div
                  className={`${styles.dropdownItem} ${
                    selectedSchema === 'ALL' ? styles.selected : ''
                  }`}
                  onClick={() => handleSchemaSelect('ALL')}
                >
                  All Schemas
                </div>
                {filteredSchemas.map((schema) => (
                  <div
                    key={schema}
                    className={`${styles.dropdownItem} ${
                      selectedSchema === schema ? styles.selected : ''
                    }`}
                    onClick={() => handleSchemaSelect(schema)}
                  >
                    {schema}
                  </div>
                ))}
                {filteredSchemas.length === 0 &&
                  searchTerm &&
                  searchTerm !== 'All Schemas' && (
                    <div className={styles.noResults}>No schemas found</div>
                  )}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonRow}>
          <button
            className={styles.button}
            onClick={handleViewConditions}
            disabled={loading || schemas.length === 0}
          >
            {loading ? 'Loading...' : 'View Schema Conditions'}
          </button>
          <button
            className={styles.button}
            onClick={handleDownload}
            disabled={loading || schemas.length === 0}
          >
            {loading ? 'Loading...' : 'Download as CSV'}
          </button>
        </div>
        {error && <div className={styles.errorMsg}>{error}</div>}
      </section>

      {showConditions && schemaConditions.length > 0 && (
        <section className={styles.conditionsCard}>
          <div className={styles.tableHeader}>
            <h2>Schema Conditions</h2>
            <button
              className={styles.copyButton}
              onClick={handleCopyToClipboard}
              title="Copy table data to clipboard for pasting into Google Sheets"
            >
              {copySuccess ? '✓ Copied!' : '📋 Copy to Clipboard'}
            </button>
          </div>
          <div className={styles.conditionsTable}>
            <table>
              <thead>
                <tr>
                  <th>Schema ID</th>
                  <th>Schema Type</th>
                  <th>Discount</th>
                  <th>Client Subsidy</th>
                  <th>Discount Schedule</th>
                  <th>Discount UI</th>
                  <th>Expiring Days</th>
                  <th>Price Display Type</th>
                  <th>Platform</th>
                </tr>
              </thead>
              <tbody>
                {schemaConditions.map((condition, index) => (
                  <tr key={index}>
                    <td>{condition.schemaId || '-'}</td>
                    <td>{condition.schemaType || '-'}</td>
                    <td>
                      {formatDiscountForDisplay(condition.discountAmount)}
                    </td>
                    <td>
                      {formatDiscountForDisplay(
                        condition.sharedSubsidyClientDiscount,
                      )}
                    </td>
                    <td className={styles.scheduleCell}>
                      {formatTimeBoundSchedule(condition.timeBoundSchedule)}
                    </td>
                    <td>{condition.discountUI ? 'TRUE' : '-'}</td>
                    <td>{condition.expiringDays || '-'}</td>
                    <td>{condition.priceDisplayType || '-'}</td>
                    <td>{condition.platform || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
};

export default SchemaConditionsLanding;
